import { buildBrandScopedPath } from "./brand-navigation";

export const WORKSPACE_RETURN_SOURCES = ["inbox", "activity", "outputs"] as const;

export type WorkspaceReturnSource = (typeof WORKSPACE_RETURN_SOURCES)[number];

export type WorkspaceReturnLocationState = {
  workspaceReturnSource?: WorkspaceReturnSource;
};

type LocationLike = {
  search: string;
  state?: unknown;
};

export function isWorkspaceReturnSource(value: unknown): value is WorkspaceReturnSource {
  return typeof value === "string" && (WORKSPACE_RETURN_SOURCES as readonly string[]).includes(value);
}

export function workspaceReturnLabel(source: WorkspaceReturnSource): string {
  return source === "inbox" ? "Inbox" : source === "outputs" ? "Outputs" : "Runs";
}

export function workspaceReturnPath(source: WorkspaceReturnSource, brandId?: string | null): string {
  const path = source === "inbox" ? "/inbox" : source === "outputs" ? "/outputs" : "/runs";
  return buildBrandScopedPath(path, brandId);
}

/**
 * A durable object may be opened from more than one standalone page. Keep only
 * an allow-listed source in the URL; never accept an arbitrary return URL.
 */
export function buildWorkspaceHandoffPath(
  path: string,
  brandId: string | null | undefined,
  source: WorkspaceReturnSource,
): string {
  const scoped = buildBrandScopedPath(path, brandId);
  const url = new URL(scoped, "http://localhost");
  url.searchParams.set("return_to", source);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function workspaceReturnLocationState(
  source: WorkspaceReturnSource,
): WorkspaceReturnLocationState {
  return { workspaceReturnSource: source };
}

export function resolveWorkspaceReturn(
  location: LocationLike,
  authoritativeBrandId?: string | null,
): {
  source: WorkspaceReturnSource;
  href: string;
  label: string;
} {
  const state = location.state;
  const stateSource = state && typeof state === "object"
    ? (state as WorkspaceReturnLocationState).workspaceReturnSource
    : undefined;
  const urlSource = new URLSearchParams(location.search).get("return_to");
  const source = isWorkspaceReturnSource(stateSource)
    ? stateSource
    : isWorkspaceReturnSource(urlSource)
      ? urlSource
      : "activity";
  const brandId = authoritativeBrandId ?? new URLSearchParams(location.search).get("brandId");
  return {
    source,
    href: workspaceReturnPath(source, brandId),
    label: workspaceReturnLabel(source),
  };
}
